import React from "react";
import { getSessionDetails } from "../../utils/userSession";
import moment from "moment";
import TitledDialog from "../TitledDialog";
import { FormattedMessage } from "react-intl";
import { Button, Typography, Paper } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { styled } from "@mui/material/styles";
import { labelTranslations } from "./common";

export type UserSessionDialogProps = {
    onHide: () => void;
    onExited?: () => void;
    onDelete: () => void;
    session: any;
    show: boolean;
    disableDeleteCurrentSession: boolean;
};

// Styled components for the grid
const LabelGrid = styled(Grid)(({ theme }) => ({
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(1.5),
    fontWeight: "bold",
    borderBottom: `1px solid ${theme.palette.divider}`,
}));

const ValueGrid = styled(Grid)(({ theme }) => ({
    padding: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
}));

LabelGrid.defaultProps = {
    size: { xs: 12, sm: 4 },
};

ValueGrid.defaultProps = {
    size: { xs: 12, sm: 8 },
};

const SessionDetailsContainer = styled(Paper)(({ theme }) => ({
    marginTop: theme.spacing(2),
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
}));

function Label({ message }: { message: keyof typeof labelTranslations }) {
    return (
        <span>
            <FormattedMessage {...labelTranslations[message]} />
        </span>
    );
}

function unknown() {
    return <Label message="unknown" />;
}

export function getContextRows(session: any) {
    if (!session["context"]) return [];

    const res = [];
    for (const [key, value] of Object.entries(session["context"])) {
        res.push(
            <React.Fragment key={key}>
                <LabelGrid><Label message={key as keyof typeof labelTranslations} /></LabelGrid>
                <ValueGrid>{value}</ValueGrid>
            </React.Fragment>,
        );
    }
    return res;
}

interface App {
    name: string;
    [key: string]: any;
}

const UserSessionDialog = ({
    onHide,
    onExited,
    onDelete,
    session,
    show,
    disableDeleteCurrentSession,
}: UserSessionDialogProps) => {
    const { title, lastUse, location } = getSessionDetails(session);

    const getBody = () => {
        const userAgent = session["user_agent"];
        const osVersion = userAgent && userAgent["os"]["version"].length > 0 ? ` (${userAgent["os"]["version"]})` : "";

        return (
            <SessionDetailsContainer elevation={0}>
                <Grid container>
                    <LabelGrid><Label message="os" /></LabelGrid>
                    <ValueGrid>{userAgent ? `${userAgent["os"]["name"]}${osVersion}` : unknown()}</ValueGrid>

                    <LabelGrid><Label message="client" /></LabelGrid>
                    <ValueGrid>
                        {userAgent ? `${userAgent["client"]["name"]} (${userAgent["client"]["version"]})` : unknown()}
                    </ValueGrid>

                    <LabelGrid><Label message="ip" /></LabelGrid>
                    <ValueGrid>{session["ip"] || unknown()}</ValueGrid>

                    <LabelGrid><Label message="location" /></LabelGrid>
                    <ValueGrid>{location}</ValueGrid>

                    <LabelGrid><Label message="created" /></LabelGrid>
                    <ValueGrid>
                        {moment(session["created"]).format("LLL")}
                        {` (${moment
                            .duration(moment.duration(moment(session["created"]).diff(moment())))
                            .humanize(true)})`}
                    </ValueGrid>

                    <LabelGrid><Label message="last_use" /></LabelGrid>
                    <ValueGrid>
                        {session["last_use"] ? (
                            <span>
                                {moment(session["last_use"]).format("LLL")} ({lastUse})
                            </span>
                        ) : (
                            <Label message="never" />
                        )}
                    </ValueGrid>

                    <LabelGrid><Label message="apps" /></LabelGrid>
                    <ValueGrid>{(session["apps"] as App[]).map((app) => app.name).join(", ")}</ValueGrid>

                    {getContextRows(session)}
                </Grid>
            </SessionDetailsContainer>
        );
    };

    return (
        <TitledDialog
            open={show}
            isContentChanged={() => false}
            hide={onHide}
            onExited={onExited}
            title={
                <>
                    <Label message="dialog_title" />
                    <Typography component="span" style={{ paddingLeft: 5 }}>
                        {title}
                    </Typography>
                </>
            }
            actions={
                session["current"] && disableDeleteCurrentSession ? null : (
                    <Button onClick={onDelete} color="error">
                        <Label message="delete" />
                    </Button>
                )
            }
        >
            {getBody()}
        </TitledDialog>
    );
};

export default UserSessionDialog;
